import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/hetrixtools')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'HetrixTools Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs HetrixTools: 15 free monitors at 1-min checks vs 5 free monitors at 30 seconds, and 50 monitors at 30s from $3/mo. Compare plans." },
      { property: 'og:title', content: 'HetrixTools Alternative — Uptrack' },
      { property: 'og:description', content: "HetrixTools has 15 free monitors at 1-min checks. Uptrack has 5 free monitors at 30s, and Pro has 50 monitors at 30s for $3/mo. Compare features and pricing." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-hetrixtools.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/hetrixtools' },
    ],
    scripts: comparisonScripts('hetrixtools', "HetrixTools"),
  }),
})

const data: ComparisonData = {
  competitor: "HetrixTools",
  tagline: "HetrixTools offers uptime, blacklist, and server monitoring, with a free plan of 15 monitors at 1-minute checks. Uptrack checks every 30 seconds and adds browser checks, from $3/mo.",
  heroDescription: "These facts come from HetrixTools's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "15 monitors at 1 min" },
    { feature: "Cheapest paid plan", uptrack: "Pro $3/mo: 50 monitors", competitor: "Professional $9.95/mo: 30 monitors", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "1 min", uptrackWins: true },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "Half of locations + 1 must fail; optional minimum downtime" },
    { feature: "Check locations", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "12", uptrackWins: MONITOR_LOCATIONS.compare.wins },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Unlimited on all plans" },
    { feature: "Blacklist monitoring", uptrack: "DNSBL zones (e.g. SpamCop, Barracuda)", competitor: "Included" },
    { feature: "Server metrics agent", uptrack: "Not available", competitor: "Included (CPU, RAM, disk)" },
  ],

  painPoints: [
    {
      title: "1-minute minimum interval",
      description: "HetrixTools checks every minute on every plan, including paid plans. Uptrack checks every 30 seconds on every plan.",
    },
    {
      title: "Price for 50 monitors",
      description: "HetrixTools Professional is $9.95/mo for 30 monitors. Uptrack Pro is $3/mo for 50 monitors.",
    },
    {
      title: "Browser flows on the entry plan",
      description: "Uptrack Pro includes 5 Playwright browser monitors for $3/mo. Each run follows a flow such as login or checkout and saves a screenshot at each step, so you see which step broke.",
    },
  ],

  differentiators: [
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "30-second checks on every plan: Pro is $3/mo for 50 monitors, Team is $20/mo for 200, Business is $79/mo for 1,000.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Status pages on every plan: 1 on free, 5 on Pro, unlimited on Team and Business, with email subscriber notifications on paid plans.",
    "Escalation policies on Team and Business route an unacknowledged alert to the next person after a delay you set.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need blacklist monitoring alongside uptime checks, HetrixTools includes it on its free plan.",
    "If you need Spamhaus blocklist results, HetrixTools may serve you better. Spamhaus may not answer Uptrack's DNSBL lookups.",
    "If you need server metrics (CPU, RAM, disk, RAID health), HetrixTools has an agent-based server monitor. Uptrack monitors from outside.",
    "If you need SMTP monitoring, HetrixTools can check mail server authentication. Uptrack doesn't monitor SMTP.",
    "If you need more than 5 free monitors, HetrixTools offers 15 monitors at 1-minute checks. Uptrack's free plan is 5 monitors at 30 seconds from one location.",
  ],
}
